import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, useTheme, Tooltip, keyframes } from '@mui/material';
import onlineStatusService from '../../services/OnlineStatusService';

interface OnlineIndicatorProps {
   userId: string;
   showText?: boolean;
   size?: 'small' | 'medium';
   showLastSeen?: boolean;
}

// Pulse animation for online status
const pulseAnimation = keyframes`
   0% {
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
   }
   70% {
      box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
   }
   100% {
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
   }
`;

const OnlineIndicator: React.FC<OnlineIndicatorProps> = ({
   userId,
   showText = false,
   size = 'small',
   showLastSeen = false,
}) => {
   const theme = useTheme();
   const [isOnline, setIsOnline] = useState(false);
   const [userStatus, setUserStatus] = useState<{
      isOnline: boolean;
      lastSeen?: Date;
      username?: string;
   } | null>(null);
   const [statusTransition, setStatusTransition] = useState<'online' | 'offline' | null>(null);

   const dotSize = size === 'small' ? 8 : 12;

   // Memoized status update callback to prevent unnecessary re-renders
   const updateStatus = useCallback(
      (onlineUsers: Map<string, any>) => {
         const status = onlineUsers.get(userId);
         const newIsOnline = status?.isOnline || false;

         // Update state immediately without checking previous state to avoid conflicts
         setIsOnline((prevIsOnline) => {
            // Only set transition if status actually changed
            if (newIsOnline !== prevIsOnline) {
               setStatusTransition(newIsOnline ? 'online' : 'offline');
               setTimeout(() => setStatusTransition(null), 1000); // Clear transition after 1 second
            }
            return newIsOnline;
         });

         setUserStatus(status || null);
      },
      [userId] // Remove isOnline from dependencies to prevent circular updates
   );

   // Initialize and subscribe to status updates
   useEffect(() => {
      // Get initial status
      const initialStatus = onlineStatusService.getUserStatus(userId);

      if (initialStatus) {
         setIsOnline(initialStatus.isOnline);
         setUserStatus(initialStatus);
      } else {
         // Unknown user, set as offline
         setIsOnline(false);
         setUserStatus(null);
      }

      // Subscribe to updates
      const unsubscribe = onlineStatusService.subscribe(updateStatus);

      return () => {
         unsubscribe();
      };
   }, [userId, updateStatus]);

   // Format last seen time
   const formatLastSeen = (lastSeen: Date) => {
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - lastSeen.getTime()) / 1000);

      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

      return lastSeen.toLocaleDateString();
   };

   // Get status text with more detail
   const getStatusText = () => {
      if (isOnline) {
         return statusTransition === 'online' ? 'Just came online' : 'Online';
      } else {
         if (showLastSeen && userStatus?.lastSeen) {
            return `Last seen ${formatLastSeen(userStatus.lastSeen)}`;
         }
         return statusTransition === 'offline' ? 'Just went offline' : 'Offline';
      }
   };

   // Get tooltip text
   const getTooltipText = () => {
      if (isOnline) {
         return `${userStatus?.username || 'User'} is online`;
      } else {
         if (userStatus?.lastSeen) {
            return `${userStatus?.username || 'User'} was last seen ${formatLastSeen(userStatus.lastSeen)}`;
         }
         return `${userStatus?.username || 'User'} is offline`;
      }
   };

   const statusDot = (
      <Box
         sx={{
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            backgroundColor: isOnline ? theme.palette.success.main : theme.palette.grey[400],
            flexShrink: 0,
            position: 'relative',
            transition: 'all 0.3s ease-in-out',
            transform: statusTransition ? 'scale(1.2)' : 'scale(1)',
            animation: isOnline ? `${pulseAnimation} 2s infinite` : 'none',
            border: statusTransition ? '2px solid' : 'none',
            borderColor:
               statusTransition === 'online'
                  ? theme.palette.success.light
                  : theme.palette.grey[300],
            '&::after': statusTransition
               ? {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor:
                       statusTransition === 'online'
                          ? theme.palette.success.main
                          : theme.palette.grey[400],
                    opacity: 0.3,
                    animation: `${pulseAnimation} 1s ease-out`,
                 }
               : {},
         }}
      />
   );

   return (
      <Tooltip title={getTooltipText()} placement='top' arrow enterDelay={500} leaveDelay={200}>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
            {statusDot}
            {showText && (
               <Typography
                  variant='caption'
                  color={isOnline ? 'success.main' : 'text.secondary'}
                  sx={{
                     fontSize: size === 'small' ? '0.75rem' : '0.875rem',
                     fontWeight: statusTransition ? 'bold' : 'normal',
                     transition: 'all 0.3s ease-in-out',
                  }}
               >
                  {getStatusText()}
               </Typography>
            )}
         </Box>
      </Tooltip>
   );
};

export default OnlineIndicator;
